/**
 * GroupedFormQuestion Component
 * Renders multiple related questions in a single form
 * Used for consolidating Yes/No branch questions (e.g., pond profile details)
 */

import React, { useState, useEffect } from "react";
import ImagePasteArea from "./ImagePasteArea";
import LabeledImageUpload from "./LabeledImageUpload";

const GroupedFormQuestion = ({
  question,
  currentData,
  onAnswer,
  onBack,
  canGoBack = false,
  projectId,
  clientId,
  currentSection,
}) => {
  const [formData, setFormData] = useState({});
  const [showWarnings, setShowWarnings] = useState({});

  // Initialize form data from currentData
  useEffect(() => {
    const initialData = {};
    if (question.fields) {
      question.fields.forEach((field) => {
        // Ensure all fields have a defined value (never undefined)
        if (
          currentData &&
          currentData[field.field] !== undefined &&
          currentData[field.field] !== null
        ) {
          // For image arrays, ensure we create a fresh copy to avoid reference sharing
          if (
            field.type === "image" &&
            Array.isArray(currentData[field.field])
          ) {
            initialData[field.field] = [...currentData[field.field]];
          } else {
            initialData[field.field] = currentData[field.field];
          }
        } else {
          // Always initialize with a defined value
          // Each image field gets its own fresh empty array
          initialData[field.field] = field.type === "image" ? [] : "";
        }

        // Handle NA fields for elevations - always defined boolean
        if (field.type === "elevation" && field.naField) {
          initialData[field.naField] = currentData?.[field.naField] === true;
        }
      });
    }
    setFormData(initialData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question]); // Only re-initialize when question changes, not when currentData updates

  const handleFieldChange = (fieldName, value) => {
    // For arrays (like images), ensure we're storing a fresh copy
    const valueToStore = Array.isArray(value) ? [...value] : value;

    setFormData((prev) => ({
      ...prev,
      [fieldName]: valueToStore,
    }));

    // Auto-save to parent (also send fresh copy)
    onAnswer(fieldName, valueToStore);
  };

  const handleElevationNA = (field) => {
    const naField = field.naField;
    const isNA = !formData[naField];

    setFormData((prev) => ({
      ...prev,
      [naField]: isNA,
      [field.field]: isNA ? null : prev[field.field],
    }));

    // Save both fields
    onAnswer(naField, isNA);
    if (isNA) {
      onAnswer(field.field, null);
    }
  };

  const handleSubmit = () => {
    // Validate required fields (optional - can show warnings but still allow submit)
    const warnings = {};
    let hasWarnings = false;

    if (question.fields) {
      question.fields.forEach((field) => {
        if (
          field.required &&
          !formData[field.field] &&
          field.type !== "image"
        ) {
          warnings[field.field] = true;
          hasWarnings = true;
        }
      });
    }

    if (hasWarnings) {
      setShowWarnings(warnings);
      // Still allow submission after warning
      setTimeout(() => setShowWarnings({}), 2000);
    }

    // Navigate to next question
    setTimeout(() => {
      if (question.next) {
        onAnswer("_navigate", question.next, formData);
      }
    }, 50);
  };

  const renderField = (field, index) => {
    // Check conditional rendering
    if (field.conditionalOn) {
      const conditionField = field.conditionalOn.field;
      const conditionValue = field.conditionalOn.value;
      const currentConditionValue = formData[conditionField];

      // Don't render if condition is not met
      if (currentConditionValue !== conditionValue) {
        return null;
      }
    }

    // Ensure value is always defined (never undefined)
    const value =
      formData[field.field] !== undefined && formData[field.field] !== null
        ? formData[field.field]
        : "";
    const isNA = field.naField ? formData[field.naField] === true : false;

    switch (field.type) {
      case "sheet_number":
      case "text":
        return (
          <div key={field.field} style={{ marginBottom: "15px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "bold",
                color: "var(--text-primary)",
              }}
            >
              {field.text}
            </label>
            {showWarnings[field.field] && (
              <div
                style={{
                  padding: "5px",
                  backgroundColor: "var(--status-warning-bg)",
                  border: "1px solid var(--status-warning)",
                  marginBottom: "5px",
                  fontSize: "12px",
                  color: "var(--status-warning)",
                }}
              >
                Warning: Field is empty
              </div>
            )}
            <input
              type="text"
              value={value}
              onChange={(e) => handleFieldChange(field.field, e.target.value)}
              placeholder={field.placeholder || "Enter value..."}
              style={{
                padding: "8px",
                fontSize: "14px",
                border: "1px solid var(--border-secondary)",
                width: "100%",
                maxWidth: "400px",
                backgroundColor: "var(--surface-tertiary)",
                color: "var(--text-primary)",
              }}
            />
          </div>
        );

      case "elevation":
        return (
          <div key={field.field} style={{ marginBottom: "15px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "bold",
                color: "var(--text-primary)",
              }}
            >
              {field.text}
            </label>
            {showWarnings[field.field] && (
              <div
                style={{
                  padding: "5px",
                  backgroundColor: "var(--status-warning-bg)",
                  border: "1px solid var(--status-warning)",
                  marginBottom: "5px",
                  fontSize: "12px",
                  color: "var(--status-warning)",
                }}
              >
                Warning: Elevation is empty
              </div>
            )}
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <input
                type="text"
                value={isNA ? "N/A" : value}
                onChange={(e) =>
                  !isNA && handleFieldChange(field.field, e.target.value)
                }
                readOnly={isNA}
                placeholder="Enter elevation..."
                style={{
                  padding: "8px",
                  fontSize: "14px",
                  border: "1px solid var(--border-secondary)",
                  width: "200px",
                  backgroundColor: isNA
                    ? "var(--surface-disabled)"
                    : "var(--surface-tertiary)",
                  color: isNA ? "var(--text-disabled)" : "var(--text-primary)",
                  cursor: isNA ? "not-allowed" : "text",
                }}
              />
              {field.allowNA && (
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isNA}
                    onChange={() => handleElevationNA(field)}
                  />
                  N/A
                </label>
              )}
            </div>
          </div>
        );

      case "boolean":
        return (
          <div key={field.field} style={{ marginBottom: "15px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "10px",
                fontWeight: "bold",
                color: "var(--text-primary)",
              }}
            >
              {field.text}
            </label>
            <div style={{ display: "flex", gap: "15px" }}>
              <button
                onClick={() => handleFieldChange(field.field, true)}
                style={{
                  padding: "10px 30px",
                  fontSize: "14px",
                  border: "1px solid var(--border-primary)",
                  backgroundColor:
                    value === true
                      ? "var(--surface-hover)"
                      : "var(--surface-tertiary)",
                  cursor: "pointer",
                  fontWeight: value === true ? "bold" : "normal",
                  color: "var(--text-primary)",
                }}
              >
                Yes
              </button>
              <button
                onClick={() => handleFieldChange(field.field, false)}
                style={{
                  padding: "10px 30px",
                  fontSize: "14px",
                  border: "1px solid var(--border-primary)",
                  backgroundColor:
                    value === false
                      ? "var(--surface-hover)"
                      : "var(--surface-tertiary)",
                  cursor: "pointer",
                  fontWeight: value === false ? "bold" : "normal",
                  color: "var(--text-primary)",
                }}
              >
                No
              </button>
            </div>
          </div>
        );

      case "choice":
        return (
          <div key={field.field} style={{ marginBottom: "15px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "10px",
                fontWeight: "bold",
                color: "var(--text-primary)",
              }}
            >
              {field.text}
            </label>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              {field.choices &&
                field.choices.map((choice) => (
                  <button
                    key={choice}
                    onClick={() => handleFieldChange(field.field, choice)}
                    style={{
                      padding: "10px 20px",
                      fontSize: "14px",
                      border: "1px solid var(--border-primary)",
                      backgroundColor:
                        value === choice
                          ? "var(--surface-hover)"
                          : "var(--surface-tertiary)",
                      cursor: "pointer",
                      fontWeight: value === choice ? "bold" : "normal",
                      color: "var(--text-primary)",
                    }}
                  >
                    {choice}
                  </button>
                ))}
            </div>
          </div>
        );

      case "image":
        // Use labeled upload by default (can be disabled with useLabeledUpload: false)
        const ImageUploadComponent =
          field.useLabeledUpload === false
            ? ImagePasteArea
            : LabeledImageUpload;

        return (
          <div key={field.field} style={{ marginBottom: "15px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "bold",
                color: "var(--text-primary)",
              }}
            >
              {field.text}
            </label>
            <ImageUploadComponent
              images={formData[field.field] || []}
              onImagesChange={(images) =>
                handleFieldChange(field.field, images)
              }
              projectId={projectId}
              clientId={clientId}
            />
          </div>
        );

      default:
        return null;
    }
  };

  // Get item name based on section
  const getItemName = () => {
    const nameFields = {
      ponds: "pond_name",
      walls: "wall_name_location",
      curb: "curb_type_name",
      "ada-ramps": "ramp_type",
    };
    const nameField = nameFields[currentSection];
    return currentData?.[nameField] || null;
  };

  const itemName = getItemName();

  return (
    <div
      className="grouped-form-question"
      style={{ padding: "20px", backgroundColor: "var(--surface-primary)" }}
    >
      {/* Show item name if available (for looped sections) */}
      {itemName && (
        <div
          style={{
            marginBottom: "20px",
            paddingBottom: "15px",
            borderBottom: "2px solid var(--border-secondary)",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "20px",
              color: "var(--text-primary)",
            }}
          >
            {itemName}
          </h2>
        </div>
      )}

      {/* Show parent yes/no question with greyed buttons */}
      {question.parentQuestion && (
        <div style={{ marginBottom: "30px" }}>
          <h3 style={{ marginBottom: "15px", color: "var(--text-secondary)" }}>
            {question.parentQuestion}
          </h3>
          <div style={{ display: "flex", gap: "15px" }}>
            <button
              disabled
              style={{
                padding: "15px 40px",
                fontSize: "16px",
                border: "1px solid var(--border-primary)",
                backgroundColor: "var(--surface-hover)",
                cursor: "not-allowed",
                fontWeight: "bold",
              }}
            >
              Yes
            </button>
            <button
              disabled
              style={{
                padding: "15px 40px",
                fontSize: "16px",
                border: "1px solid var(--border-primary)",
                backgroundColor: "var(--surface-secondary)",
                cursor: "not-allowed",
              }}
            >
              No
            </button>
          </div>
        </div>
      )}

      <h3 style={{ marginBottom: "20px", color: "var(--text-primary)" }}>
        {question.text}
      </h3>

      {/* Render all fields */}
      <div
        style={{
          backgroundColor: "var(--surface-secondary)",
          padding: "20px",
          border: "1px solid var(--border-secondary)",
          borderRadius: "4px",
        }}
      >
        {question.fields &&
          question.fields.map((field, index) => renderField(field, index))}
      </div>

      {/* Submit button */}
      <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
        {canGoBack && (
          <button
            onClick={onBack}
            style={{
              padding: "12px 40px",
              fontSize: "16px",
              border: "1px solid var(--border-primary)",
              backgroundColor: "var(--surface-tertiary)",
              cursor: "pointer",
              color: "var(--text-primary)",
            }}
          >
            ← Back
          </button>
        )}
        <button
          onClick={handleSubmit}
          style={{
            padding: "var(--space-3) var(--space-8)",
            fontSize: "var(--text-base)",
            fontWeight: "var(--font-semibold)",
            border: "1px solid var(--cta-blue)",
            backgroundColor: "var(--cta-blue)",
            color: "white",
            cursor: "pointer",
            borderRadius: "var(--radius-md)",
            transition: "all var(--transition-fast)",
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = "#2563eb";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = "var(--cta-blue)";
          }}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default GroupedFormQuestion;
